import { forwardRef, ReactNode, type Ref } from 'react';

// mui
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  useMediaQuery,
  useTheme,
  type SlideProps,
} from '@mui/material';

// components
import { Icon } from '@/components/atoms/Icon/Icon';

const Transition = forwardRef(function Transition(
  props: SlideProps,
  ref: Ref<unknown>,
) {
  return <Slide direction='up' ref={ref} {...props} />;
});

export interface BottomSheetDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Title shown with a close button. Omit for a chromeless sheet. */
  title?: ReactNode;
  /** Title alignment. `left` (default) for detail views; `center` for forms. */
  titleAlign?: 'left' | 'center';
  /** Scrollable body. */
  children: ReactNode;
  /** Sticky footer (action buttons, a banner, …). */
  footer?: ReactNode;
  /** Desktop max width. */
  maxWidth?: 'xs' | 'sm' | 'md';
  /** Render above another open modal (e.g. a confirm sheet over a drawer). */
  aboveModal?: boolean;
  /** Use the warm (cream) background instead of white. */
  warm?: boolean;
}

/**
 * BottomSheetDialog is the shared dialog shell: a centred dialog on desktop that
 * becomes a bottom sheet on mobile (slide-up, rounded top, flush to the bottom —
 * see the `bottom-sheet` styles in theme/mui/components.ts). Provides an optional
 * left-aligned title + close button, a scrollable body, and an optional footer.
 */
export const BottomSheetDialog = ({
  isOpen,
  onClose,
  title,
  titleAlign = 'left',
  children,
  footer,
  maxWidth = 'sm',
  aboveModal = false,
  warm = false,
}: BottomSheetDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const classes = [
    'bottom-sheet',
    aboveModal && 'bottom-sheet--above-modal',
    warm && 'bottom-sheet--warm',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      scroll='paper'
      className={classes}
      {...(isMobile ? { slots: { transition: Transition } } : {})}
    >
      {title != null && (
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              position: 'relative',
            }}
          >
            <Box
              component='span'
              sx={{
                flex: titleAlign === 'center' ? 1 : 'initial',
                textAlign: titleAlign,
              }}
            >
              {title}
            </Box>
            <IconButton
              aria-label='Close'
              onClick={onClose}
              edge='end'
              size='small'
              sx={
                titleAlign === 'center'
                  ? { position: 'absolute', right: 0, top: 0 }
                  : undefined
              }
            >
              <Icon icon='xmark' fontSize={16} />
            </IconButton>
          </Box>
        </DialogTitle>
      )}
      <DialogContent>{children}</DialogContent>
      {footer != null && <DialogActions>{footer}</DialogActions>}
    </Dialog>
  );
};

BottomSheetDialog.displayName = 'BottomSheetDialog';
export default BottomSheetDialog;
